import OpCodes from './OpCodes';
import type { OpCodeDef } from './types';

// A decoded instruction's immediate operands, as resolved values (indices, constants,
// byte vectors) keyed by a discriminated `type`. Replaces the former positional any[].
export type DecodedImmediate =
  | { type: 'None' }
  | { type: 'BlockSignature'; blockType: number }
  | { type: 'VarInt32'; value: number }
  | { type: 'VarInt64'; value: bigint }
  | { type: 'Float32'; value: number }
  | { type: 'Float64'; value: number }
  | { type: 'VarUInt1'; value: number }
  | { type: 'VarUInt32'; value: number }
  | { type: 'RelativeDepth'; depth: number }
  | { type: 'BranchTable'; targets: number[]; defaultTarget: number }
  | { type: 'Function'; functionIndex: number }
  | { type: 'IndirectFunction'; typeIndex: number; tableIndex: number }
  | { type: 'Local'; localIndex: number }
  | { type: 'Global'; globalIndex: number }
  | { type: 'MemoryImmediate'; alignment: number; offset: number }
  | { type: 'V128Const'; bytes: Uint8Array }
  | { type: 'ShuffleMask'; mask: Uint8Array }
  | { type: 'LaneIndex'; lane: number }
  | { type: 'TypeIndexField'; typeIndex: number; fieldIndex: number }
  | { type: 'TypeIndexIndex'; typeIndex: number; index: number }
  | { type: 'HeapType'; heapType: number }
  | { type: 'BrOnCast'; flags: number; depth: number; heapType1: number; heapType2: number };

export type DecodedImmediateType = DecodedImmediate['type'];

// The immediate's numeric operands in encoding order. Used where an instruction carries
// its immediates through generically as const arguments (GC / bulk-memory intrinsics).
// i64 constants and 16-byte vectors are not plain numbers and are intentionally excluded.
export function immediateNumbers(immediate: DecodedImmediate): number[] {
  switch (immediate.type) {
    case 'None': return [];
    case 'BlockSignature': return [immediate.blockType];
    case 'VarInt32': case 'Float32': case 'Float64': case 'VarUInt1': case 'VarUInt32': return [immediate.value];
    case 'VarInt64': return [];
    case 'RelativeDepth': return [immediate.depth];
    case 'BranchTable': return [...immediate.targets, immediate.defaultTarget];
    case 'Function': return [immediate.functionIndex];
    case 'IndirectFunction': return [immediate.typeIndex, immediate.tableIndex];
    case 'Local': return [immediate.localIndex];
    case 'Global': return [immediate.globalIndex];
    case 'MemoryImmediate': return [immediate.alignment, immediate.offset];
    case 'V128Const': case 'ShuffleMask': return [];
    case 'LaneIndex': return [immediate.lane];
    case 'TypeIndexField': return [immediate.typeIndex, immediate.fieldIndex];
    case 'TypeIndexIndex': return [immediate.typeIndex, immediate.index];
    case 'HeapType': return [immediate.heapType];
    case 'BrOnCast': return [immediate.flags, immediate.depth, immediate.heapType1, immediate.heapType2];
  }
}

// All operands of an immediate in encoding order for display (unlike immediateNumbers,
// keeps i64 constants and flattens 16-byte vectors to their bytes).
export function immediateOperands(immediate: DecodedImmediate): (number | bigint)[] {
  switch (immediate.type) {
    case 'VarInt64': return [immediate.value];
    case 'V128Const': return Array.from(immediate.bytes);
    case 'ShuffleMask': return Array.from(immediate.mask);
    default: return immediateNumbers(immediate);
  }
}

export interface DecodedInstruction {
  opCode: OpCodeDef;
  immediates: DecodedImmediate;
  offset: number;
  length: number;
}

// Build reverse lookup maps from opcode bytes to OpCodeDef
const singleByteOpcodes: Map<number, OpCodeDef> = new Map();
const prefixedOpcodes: Map<number, Map<number, OpCodeDef>> = new Map();

for (const [, opCode] of Object.entries(OpCodes)) {
  const op = opCode as OpCodeDef;
  if (op.prefix !== undefined) {
    if (!prefixedOpcodes.has(op.prefix)) {
      prefixedOpcodes.set(op.prefix, new Map());
    }
    prefixedOpcodes.get(op.prefix)!.set(op.value, op);
  } else {
    singleByteOpcodes.set(op.value, op);
  }
}

export default class InstructionDecoder {
  private buffer: Uint8Array;
  private offset: number;

  constructor(buffer: Uint8Array) {
    this.buffer = buffer;
    this.offset = 0;
  }

  decode(): DecodedInstruction[] {
    const instructions: DecodedInstruction[] = [];
    this.offset = 0;

    while (this.offset < this.buffer.length) {
      const startOffset = this.offset;
      const byte = this.buffer[this.offset++];

      let opCode: OpCodeDef | undefined;

      // Check for prefixed opcodes (0xFC = sat-trunc/bulk-memory, 0xFB = GC, 0xFD = SIMD, 0xFE = threads)
      if (prefixedOpcodes.has(byte)) {
        const subMap = prefixedOpcodes.get(byte)!;
        const subOpcode = this.readVarUInt32();
        opCode = subMap.get(subOpcode);
        if (!opCode) {
          // Unknown prefixed opcode — skip to end
          break;
        }
      } else {
        opCode = singleByteOpcodes.get(byte);
        if (!opCode) {
          // Unknown opcode — skip to end
          break;
        }
      }

      const immediates = this.decodeImmediates(opCode);
      instructions.push({
        opCode,
        immediates,
        offset: startOffset,
        length: this.offset - startOffset,
      });

      // Stop at end opcode
      if (opCode === OpCodes.end) {
        break;
      }
    }

    return instructions;
  }

  static decodeInitExpr(bytes: Uint8Array): DecodedInstruction[] {
    const decoder = new InstructionDecoder(bytes);
    return decoder.decode();
  }

  static decodeFunctionBody(bytes: Uint8Array): DecodedInstruction[] {
    const decoder = new InstructionDecoder(bytes);
    return decoder.decodeAll();
  }

  decodeAll(): DecodedInstruction[] {
    const instructions: DecodedInstruction[] = [];
    this.offset = 0;
    let depth = 0;

    while (this.offset < this.buffer.length) {
      const startOffset = this.offset;
      const byte = this.buffer[this.offset++];

      let opCode: OpCodeDef | undefined;

      if (prefixedOpcodes.has(byte)) {
        const subMap = prefixedOpcodes.get(byte)!;
        const subOpcode = this.readVarUInt32();
        opCode = subMap.get(subOpcode);
        if (!opCode) {
          break;
        }
      } else {
        opCode = singleByteOpcodes.get(byte);
        if (!opCode) {
          break;
        }
      }

      const immediates = this.decodeImmediates(opCode);
      instructions.push({
        opCode,
        immediates,
        offset: startOffset,
        length: this.offset - startOffset,
      });

      if (opCode === OpCodes.block || opCode === OpCodes.loop || opCode === OpCodes.if || opCode === OpCodes.try) {
        depth++;
      } else if (opCode === OpCodes.end) {
        if (depth === 0) {
          break;
        }
        depth--;
      }
    }

    return instructions;
  }

  private decodeImmediates(opCode: OpCodeDef): DecodedImmediate {
    if (!opCode.immediate) {
      return { type: 'None' };
    }

    switch (opCode.immediate) {
      case 'BlockSignature':
        return { type: 'BlockSignature', blockType: this.readVarInt7() };
      case 'VarInt32':
        return { type: 'VarInt32', value: this.readVarInt32() };
      case 'VarInt64':
        return { type: 'VarInt64', value: this.readVarInt64() };
      case 'Float32':
        return { type: 'Float32', value: this.readFloat32() };
      case 'Float64':
        return { type: 'Float64', value: this.readFloat64() };
      case 'VarUInt1':
        return { type: 'VarUInt1', value: this.buffer[this.offset++] & 1 };
      case 'VarUInt32':
        return { type: 'VarUInt32', value: this.readVarUInt32() };
      case 'RelativeDepth':
        return { type: 'RelativeDepth', depth: this.readVarUInt32() };
      case 'BranchTable': {
        const count = this.readVarUInt32();
        const targets: number[] = [];
        for (let i = 0; i < count; i++) {
          targets.push(this.readVarUInt32());
        }
        return { type: 'BranchTable', targets, defaultTarget: this.readVarUInt32() };
      }
      case 'Function':
        return { type: 'Function', functionIndex: this.readVarUInt32() };
      case 'IndirectFunction':
        return { type: 'IndirectFunction', typeIndex: this.readVarUInt32(), tableIndex: this.readVarUInt32() };
      case 'Local':
        return { type: 'Local', localIndex: this.readVarUInt32() };
      case 'Global':
        return { type: 'Global', globalIndex: this.readVarUInt32() };
      case 'MemoryImmediate':
        return { type: 'MemoryImmediate', alignment: this.readVarUInt32(), offset: this.readVarUInt32() };
      case 'V128Const':
        return { type: 'V128Const', bytes: this.readFixedBytes(16) };
      case 'ShuffleMask':
        return { type: 'ShuffleMask', mask: this.readFixedBytes(16) };
      case 'LaneIndex':
        return { type: 'LaneIndex', lane: this.buffer[this.offset++] };
      case 'TypeIndexField':
        return { type: 'TypeIndexField', typeIndex: this.readVarUInt32(), fieldIndex: this.readVarUInt32() };
      case 'TypeIndexIndex':
        return { type: 'TypeIndexIndex', typeIndex: this.readVarUInt32(), index: this.readVarUInt32() };
      case 'HeapType':
        return { type: 'HeapType', heapType: this.readVarInt32() };
      case 'BrOnCast': {
        const flags = this.buffer[this.offset++];
        const depth = this.readVarUInt32();
        const heapType1 = this.readVarInt32();
        const heapType2 = this.readVarInt32();
        return { type: 'BrOnCast', flags, depth, heapType1, heapType2 };
      }
      default:
        return { type: 'None' };
    }
  }

  private readFixedBytes(count: number): Uint8Array {
    const bytes = new Uint8Array(count);
    for (let i = 0; i < count; i++) {
      bytes[i] = this.buffer[this.offset++];
    }
    return bytes;
  }

  // --- LEB128 readers ---

  private readVarUInt32(): number {
    let result = 0;
    let shift = 0;
    let byte: number;
    do {
      byte = this.buffer[this.offset++];
      result |= (byte & 0x7f) << shift;
      shift += 7;
    } while (byte & 0x80);
    return result >>> 0;
  }

  private readVarInt7(): number {
    const byte = this.buffer[this.offset++];
    return byte & 0x40 ? byte | 0xffffff80 : byte & 0x7f;
  }

  private readVarInt32(): number {
    let result = 0;
    let shift = 0;
    let byte: number;
    do {
      byte = this.buffer[this.offset++];
      result |= (byte & 0x7f) << shift;
      shift += 7;
    } while (byte & 0x80);
    if (shift < 32 && byte & 0x40) {
      result |= -(1 << shift);
    }
    return result;
  }

  private readVarInt64(): bigint {
    let result = 0n;
    let shift = 0n;
    let byte: number;
    do {
      byte = this.buffer[this.offset++];
      result |= BigInt(byte & 0x7f) << shift;
      shift += 7n;
    } while (byte & 0x80);
    if (shift < 64n && byte & 0x40) {
      result |= -(1n << shift);
    }
    return result;
  }

  private readFloat32(): number {
    const view = new DataView(this.buffer.buffer, this.buffer.byteOffset + this.offset, 4);
    this.offset += 4;
    return view.getFloat32(0, true);
  }

  private readFloat64(): number {
    const view = new DataView(this.buffer.buffer, this.buffer.byteOffset + this.offset, 8);
    this.offset += 8;
    return view.getFloat64(0, true);
  }
}
